import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Sparkles, ArrowLeft, X, RefreshCw, Lock, Loader2, AlertTriangle, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { isTransportFailure, useLatestRequest } from '../../utils/requestLifecycle';
import useAiLogJobs from '../../hooks/useAiLogJobs';
import useUserStore from '../../store/userStore';
import { setFlowPhoto, setFlowDetails, setFlowImageB64 } from './flowStore';
import { canAnalyze, analyzeDisabled } from './inputMode';
import AiLogJobsSection from '../../components/AiLogJobsSection';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import useKeyboardInset from '../../hooks/useKeyboardInset';

// Keyboard-avoidance spacing (px): the gap kept between the Analyze button and the keyboard, and the
// lowest the description's top may be scrolled to (clear of the status bar / Dynamic Island).
const KEYBOARD_GAP_PX = 12;
const NOTE_TOP_MIN_PX = 72;

// Downscale to <=1024px JPEG data-URL (keeps upload small + matches backend compression).
function downscale(file, cb) {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const max = 1024; let w = img.width, h = img.height;
      if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      try { cb(c.toDataURL('image/jpeg', 0.7)); } catch (e) { cb(''); }
    };
    img.onerror = () => cb('');
    img.src = reader.result;
  };
  reader.onerror = () => cb('');
  reader.readAsDataURL(file);
}

// POST /ai-log/jobs boundary (photo upload included). Never retried automatically by the shared
// layer (it is a write); AILogStart re-sends it ONCE, with the SAME submission_id, only after an
// ambiguous transport failure — the backend is idempotent per (user, submission_id).
export const AI_JOB_CREATE_TIMEOUT_MS = 30000;
const SUBMIT_ERROR_MSG = "We couldn't start analyzing your meal. Check your connection and try again.";

/** Create the durable job; one same-submission_id re-send after an ambiguous transport failure. */
export async function createAiLogJob(payload) {
  const send = () => api.post('/ai-log/jobs', payload, { timeout: AI_JOB_CREATE_TIMEOUT_MS, retry: false });
  try {
    return await send();
  } catch (e) {
    if (!isTransportFailure(e)) throw e;     // 4xx/5xx are answers: never re-sent
    return send();                            // identical payload => identical submission_id
  }
}

// Single-page AI entry: take a photo AND/OR write a description, then Analyze. All three modes are
// valid — photo only, description only, or both. Submission is allowed when a valid photo OR a
// non-empty description is present; nothing is ever fabricated when both are empty.
export default function AILogStart() {
  const navigate = useNavigate();
  const { showPaywall, isSubscribed } = useSubscriptionContext();
  const [photo, setPhoto] = React.useState('');      // dataURL of the chosen REAL photo
  const [note, setNote] = React.useState('');
  const [avail, setAvail] = React.useState(null);    // null=loading | { available, reason, message }
  const [picking, setPicking] = React.useState(false);
  const [pickError, setPickError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);   // durable submit in-flight -> lock CTA
  const [submitError, setSubmitError] = React.useState('');    // retryable durable-create failure
  // Processing view: shown the moment Analyze is tapped (durable path) and kept while the accepted job is
  // queued/processing; it then reflects ready (Review) / failed (Retry) from the shared jobs store.
  const [processing, setProcessing] = React.useState(false);
  const [acceptedJobId, setAcceptedJobId] = React.useState('');
  const submissionIdRef = React.useRef('');                    // stable per logical submission (reused on retry)
  const photoRef = React.useRef(null);

  // Localized keyboard-avoidance. This min-h-screen page scrolls as a whole document, `resize: 'body'`
  // never shrinks it and the Capacitor keyboard plugin turns off WKWebView's own focus scrolling, so a
  // short page has no room to lift the description. While the keyboard is open (useKeyboardInset) a
  // spacer as tall as the keyboard ends the content, and the page scrolls so the description AND
  // the Analyze button directly below it sit just above the keyboard (re-run when the height changes,
  // e.g. the predictive bar). The spacer goes away when the keyboard closes. No global keyboard config.
  const kb = useKeyboardInset();
  const noteRef = React.useRef(null);
  const analyzeRef = React.useRef(null);
  const revealNote = React.useCallback(function () {
    var noteEl = noteRef.current;
    var btn = analyzeRef.current;
    if (!kb || !noteEl || !btn || document.activeElement !== noteEl) return;
    var down = btn.getBoundingClientRect().bottom - (window.innerHeight - kb - KEYBOARD_GAP_PX);
    // How far the page may move up before the top of the description leaves the screen (short phones:
    // the description wins over the button; one already scrolled off the top is brought back).
    var noteRoom = noteEl.getBoundingClientRect().top - NOTE_TOP_MIN_PX;
    var delta = noteRoom < 0 ? noteRoom : Math.min(Math.max(down, 0), noteRoom);
    if (delta !== 0) window.scrollBy({ top: delta, behavior: 'smooth' });
  }, [kb]);
  React.useEffect(revealNote, [revealNote]);

  // Entry-point enrollment check — UX only. Backend /draft/* + /commit gates remain the real guard.
  // Lifecycle-owned: every check (initial mount, a subscription change, manual Retry) supersedes the
  // previous one — the older request is aborted and its late response ignored, so it can never
  // overwrite the newer result. Unmount aborts the in-flight check.
  const availabilityCheck = useLatestRequest();
  const checkAvailability = React.useCallback(() => {
    const req = availabilityCheck.begin();
    api.get('/ai-log/availability', { signal: req.signal })
      .then((r) => { if (req.isCurrent()) setAvail(r.data || { available: false, reason: 'login_required' }); })
      .catch((e) => {
        if (!req.isCurrent()) return;
        // 401 = genuinely signed out. Anything else (timeout, network, 5xx) means we could NOT check —
        // a connection/load error with Retry, never an account/rollout ("not enabled") message.
        const reason = e && e.response && e.response.status === 401 ? 'login_required' : 'load_error';
        setAvail({ available: false, reason });
      });
  }, [availabilityCheck]);

  // ONE effect: checks once on initial mount (whatever the subscription state) and re-checks only when
  // isSubscribed actually changes — e.g. after an in-session purchase, so AI Log unlocks without a
  // relaunch. Backend entitlement is still the authoritative gate.
  React.useEffect(() => { checkAvailability(); }, [isSubscribed, checkAvailability]);

  // Shared durable-jobs store (ONE polling loop app-wide; its reads use the finite shared lifecycle).
  const { jobs, retry: retryJob, addAccepted } = useAiLogJobs(!!(avail && avail.jobs_available));
  const ownerId = useUserStore((s) => (s.user && s.user.user_id) || null);   // signed-in account submitting
  const acceptedJob = acceptedJobId ? jobs.find((j) => j.job_id === acceptedJobId) : null;


  const pick = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                              // allow re-picking the same file
    if (!file) return;
    setPickError('');
    setPicking(true);
    downscale(file, (dataUrl) => {
      setPicking(false);
      if (dataUrl) { setPhoto(dataUrl); }
      else { setPickError('Could not read that photo. Please try another.'); }
    });
  };

  const removePhoto = () => { setPhoto(''); setPickError(''); };

  const analyze = async () => {
    const details = note.trim();
    if (!canAnalyze(photo, details)) return;          // need at least one usable input (photo or text)
    if (submitting) return;                            // rapid/double Analyze -> one request only
    // DURABLE (Phase 3): create a background job then return Home so the user can leave immediately.
    // Analysis continues server-side and surfaces on Home + AI Log. Never fabricates/commits anything.
    if (avail && avail.jobs_available) {
      // Stable idempotency key: generated ONCE per logical submission and REUSED on retry so an
      // ambiguous network failure re-hits the SAME job (backend is idempotent per user+submission_id).
      if (!submissionIdRef.current) {
        submissionIdRef.current = 'sub_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      }
      setSubmitError('');
      setSubmitting(true);
      setAcceptedJobId('');
      setProcessing(true);                             // immediately: "Analyzing your meal…"
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const input_kind = photo && details ? 'image_text' : (photo ? 'image' : 'text');
      const ownerAtSubmit = ownerId;                   // the account this submission belongs to
      const submittedLocalDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      try {
        const res = await createAiLogJob({
          submission_id: submissionIdRef.current,
          input_kind,
          user_text: details || null,
          image_base64: photo || null,
          submitted_local_date: submittedLocalDate,
          submitted_local_hour: now.getHours(),
          submitted_tz_offset_min: -now.getTimezoneOffset(),
        });
        const rawJobId = res && res.data ? res.data.job_id : null;
        const jobId = typeof rawJobId === 'string' ? rawJobId.trim() : '';
        // Register the job for the SAME signed-in account BEFORE anything unlocks Go to Home. addAccepted
        // refuses (and makes no request) when the account changed during the submission.
        const registered = !!jobId && addAccepted(ownerAtSubmit, {
          job_id: jobId, input_kind, submitted_local_date: submittedLocalDate,
          suggested_meal_type: (res.data && res.data.suggested_meal_type) || null,
        });
        if (!registered) {
          // No usable job_id (or no longer the submitting account) is NOT an acceptance: never unlock
          // Go to Home. Keep the SAME submission_id so Try again re-hits the same idempotent submission.
          setSubmitting(false);
          setSubmitError(SUBMIT_ERROR_MSG);
          return;
        }
        submissionIdRef.current = '';                  // accepted -> next genuinely new meal gets a new id
        setSubmitting(false);
        setAcceptedJobId(jobId);                       // addAccepted already started the shared poll
        return;
      } catch (e) {
        // Once jobs_available is true we NEVER fall back to the legacy synchronous flow. Surface a
        // retryable error; the SAME submission_id is kept so a retry reuses the idempotency key.
        setSubmitting(false);
        setSubmitError(SUBMIT_ERROR_MSG);
        return;
      }
    }
    setFlowDetails(details);
    setFlowImageB64(photo);                            // '' when logging by description only
    setFlowPhoto(photo);
    navigate('/ai-log/analyzing');
  };

  // ---- Enrollment gate ----
  // While the availability check is in flight: the normal "Log with AI" shell with a neutral spinner.
  // Presentation only — no form, no jobs, no access/subscription wording; the gate below is unchanged.
  if (avail === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-loading">
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="loading-back"><ArrowLeft size={18} /></button>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex items-center justify-center" role="status" aria-label="Loading">
          <Loader2 size={26} className="text-accent animate-spin" />
        </div>
      </div>
    );
  }
  // ---- Pro-required (broad release): standard Fitxess Pro upgrade experience ----
  // Distinct from controlled-rollout "not enrolled" and from "login_required". Uses the app's existing
  // paywall via SubscriptionContext.showPaywall(); no second subscription system, no "plus"/required_tier
  // or rollout/flag wording in the customer-facing copy.
  if (avail && avail.reason === 'pro_required') {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-pro-required">
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="pro-required-back"><ArrowLeft size={18} /></button>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <span className="size-16 rounded-full bg-gradient-to-br from-accent to-cyan-400 flex items-center justify-center shadow-sm"><Sparkles size={26} className="text-white" /></span>
          <h1 className="text-xl font-bold text-foreground mt-5" data-testid="pro-required-title">AI meal logging is a Fitxess Pro feature</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs" data-testid="pro-required-message">
            Snap a photo or describe your meal and get an instant nutrition estimate to review before logging. Upgrade to Fitxess Pro to unlock AI meal logging.
          </p>
          <button onClick={() => showPaywall()} className="mt-7 h-12 px-6 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity" data-testid="pro-required-cta">
            See Fitxess Pro
          </button>
          <button onClick={() => navigate(-1)} className="mt-3 text-sm text-muted-foreground hover:text-foreground py-2" data-testid="pro-required-dismiss">
            Not now
          </button>
        </div>
      </div>
    );
  }
  // ---- Could not check access (timeout / network / server error): recoverable, with Retry ----
  if (avail.reason === 'load_error') {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-availability-error">
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="availability-error-back"><ArrowLeft size={18} /></button>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <span className="size-14 rounded-full bg-secondary flex items-center justify-center"><AlertTriangle size={24} className="text-muted-foreground" /></span>
          <h1 className="text-lg font-bold text-foreground mt-4" data-testid="availability-error-title">We couldn’t connect</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="availability-error-message">Check your connection and try again.</p>
          <button onClick={() => { setAvail(null); checkAvailability(); }} className="mt-6 h-11 px-6 rounded-xl bg-accent text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="availability-retry-btn">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }
  if (!avail.available) {
    const isLogin = avail.reason === 'login_required';
    const title = isLogin ? 'Sign in to use AI photo logging'
      : avail.reason === 'feature_disabled' ? 'AI photo logging is unavailable right now'
      : 'Not enabled for this account yet';
    const body = isLogin ? 'Please sign in to analyze meal photos.'
      : (avail.message || 'AI photo logging is not enabled for this account yet. It’s rolling out to a small group first.');
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-unavailable">
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="unavailable-back"><ArrowLeft size={18} /></button>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <span className="size-14 rounded-full bg-secondary flex items-center justify-center"><Lock size={24} className="text-muted-foreground" /></span>
          <h1 className="text-lg font-bold text-foreground mt-4" data-testid="unavailable-title">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="unavailable-message">{body}</p>
          {isLogin ? (
            <button onClick={() => navigate('/login')} className="mt-6 h-11 px-6 rounded-xl bg-accent text-white text-sm font-semibold" data-testid="unavailable-login-btn">Sign in</button>
          ) : (
            <button onClick={() => navigate(-1)} className="mt-6 h-11 px-6 rounded-xl bg-secondary text-foreground text-sm font-medium border border-border/40" data-testid="unavailable-back-btn">Go back</button>
          )}
        </div>
      </div>
    );
  }

  // ---- Processing view (durable job): Analyzing… -> Ready to review / Couldn't analyze ----
  if (processing) {
    const status = submitError ? 'submit_failed'
      : !acceptedJobId ? 'starting'
      : (acceptedJob && acceptedJob.status) || 'queued';
    const analyzing = status === 'starting' || status === 'queued' || status === 'processing';
    const backToForm = () => { setProcessing(false); setAcceptedJobId(''); };
    const startNew = () => { setProcessing(false); setAcceptedJobId(''); setPhoto(''); setNote(''); setSubmitError(''); };
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-processing" data-status={status}>
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          {photo ? <img src={photo} alt="Your meal" className="w-28 h-28 rounded-2xl object-cover shadow-sm mb-5" /> : null}
          {analyzing && (
            <>
              <Loader2 size={30} className="text-accent animate-spin" />
              <h1 className="text-xl font-bold text-foreground mt-4" data-testid="processing-title">Analyzing your meal…</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs" data-testid="processing-message">
                This usually takes a few moments. You can leave this screen — we’ll keep analyzing and let you know when it’s ready to review.
              </p>
              {/* Go to Home only after the server accepted the job (valid job_id) — never while pending. */}
              {acceptedJobId ? (
                <button onClick={() => navigate('/home')} className="mt-7 h-11 px-6 rounded-xl bg-secondary text-foreground text-sm font-medium border border-border/40" data-testid="processing-home-btn">
                  Go to Home
                </button>
              ) : null}
            </>
          )}
          {status === 'ready' && (
            <>
              <Sparkles size={30} className="text-accent" />
              <h1 className="text-xl font-bold text-foreground mt-4" data-testid="processing-title">Ready to review</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs">Check the foods and portions before anything is logged.</p>
              <button onClick={() => navigate(`/ai-log/review?job_id=${encodeURIComponent(acceptedJobId)}`)} className="mt-7 h-12 px-6 rounded-xl bg-accent text-white text-sm font-semibold inline-flex items-center gap-1" data-testid="processing-review-btn">
                Review meal <ChevronRight size={16} />
              </button>
            </>
          )}
          {(status === 'failed' || status === 'submit_failed') && (
            <>
              <AlertTriangle size={28} className="text-destructive/70" />
              <h1 className="text-xl font-bold text-foreground mt-4" data-testid="processing-title">We couldn’t analyze your meal</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs" data-testid="processing-error">
                {status === 'submit_failed' ? submitError
                  : ((acceptedJob && acceptedJob.error && acceptedJob.error.message) || 'Something went wrong while analyzing. You can try again.')}
              </p>
              <button
                onClick={() => (status === 'submit_failed' ? analyze() : retryJob(acceptedJobId).catch(() => {}))}
                className="mt-7 h-12 px-6 rounded-xl bg-accent text-white text-sm font-semibold inline-flex items-center gap-2"
                data-testid="processing-retry-btn"
              >
                <RefreshCw size={16} /> Try again
              </button>
              <button onClick={status === 'submit_failed' ? backToForm : startNew} className="mt-3 text-sm text-muted-foreground hover:text-foreground py-2" data-testid="processing-edit-btn">
                {status === 'submit_failed' ? 'Edit meal' : 'Start over'}
              </button>
            </>
          )}
          {(status === 'committed' || status === 'discarded') && (
            <button onClick={startNew} className="mt-2 h-11 px-6 rounded-xl bg-accent text-white text-sm font-semibold" data-testid="processing-new-btn">Log another meal</button>
          )}
        </div>
      </div>
    );
  }

  // ---- Single page: photo selector (or preview) + optional description + Analyze ----
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="ai-log-start">
      <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="start-back"><ArrowLeft size={18} /></button>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-2" data-testid="ai-log-start-content" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.25rem)' }}>
        <AiLogJobsSection title="Your meal analyses" compact />
        <h1 className="text-2xl font-bold text-foreground leading-tight">What are you eating?</h1>
        <p className="text-sm text-muted-foreground mt-1">Take a photo, describe your meal, or use both for a more accurate estimate — you’ll review everything before it’s logged.</p>

        {/* ONE clickable photo area: preview when chosen, otherwise the picker. */}
        {photo ? (
          <div className="relative mt-5 rounded-2xl overflow-hidden">
            <img src={photo} alt="Your meal" className="w-full h-56 object-cover" data-testid="preview-photo" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent h-16" />
            <div className="absolute top-3 right-3 flex gap-2">
              <button onClick={() => photoRef.current && photoRef.current.click()} className="h-9 px-3 rounded-full bg-black/45 backdrop-blur flex items-center gap-1.5 text-white text-xs font-medium" data-testid="change-photo-btn"><RefreshCw size={13} />Change</button>
              <button onClick={removePhoto} className="size-9 rounded-full bg-black/45 backdrop-blur flex items-center justify-center text-white" data-testid="remove-photo-btn"><X size={16} /></button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => photoRef.current && photoRef.current.click()}
            className="mt-5 w-full rounded-2xl border-2 border-dashed border-border/60 bg-card py-10 flex flex-col items-center gap-3 hover:border-accent/60 transition-colors"
            data-testid="photo-select-area"
          >
            <span className="size-14 rounded-full bg-accent/15 flex items-center justify-center">
              {picking ? <Loader2 size={24} className="text-accent animate-spin" /> : <Camera size={24} className="text-accent" />}
            </span>
            <span className="text-sm font-semibold text-foreground">{picking ? 'Preparing photo…' : 'Take or choose a photo'}</span>
            <span className="text-xs text-muted-foreground">Camera or photo library</span>
          </button>
        )}

        {/* Single hidden input, no capture attribute -> native sheet offers Take Photo / Photo Library. */}
        <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="photo-input" />

        {pickError && <p className="text-xs text-destructive mt-2" data-testid="photo-pick-error">{pickError}</p>}

        {/* Description — a valid standalone input (photo is NOT required). */}
        <label className="block text-sm font-semibold text-foreground mt-6" htmlFor="start-note-input">Describe your meal</label>
        <p className="text-xs text-muted-foreground mt-1">Include foods, amounts, cooking methods, sauces, or ingredients the photo may not show.</p>
        <textarea id="start-note-input" value={note} onChange={(e) => setNote(e.target.value)} onFocus={revealNote} ref={noteRef} rows={3} placeholder="e.g. Two chicken enchiladas with 6 oz of chicken, cheese, and red enchilada sauce"
          className="w-full mt-2 px-3 py-2.5 text-sm bg-card border border-border/40 rounded-xl outline-none focus:border-accent resize-none" data-testid="start-note" />

        {/* Analyze CTA + estimate disclosure: in the normal flow directly below the description, so
            it moves down whenever the content above it grows. */}
        <div className="mt-4" data-testid="analyze-cta">
          {submitError && (
            <p className="text-xs text-destructive text-center mb-2" data-testid="analyze-submit-error">{submitError}</p>
          )}
          <button ref={analyzeRef} onClick={analyze} disabled={analyzeDisabled(photo, note, picking) || submitting} className="w-full h-12 rounded-xl bg-accent text-white font-semibold shadow-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="analyze-btn">
            {submitting ? <><Loader2 size={18} className="animate-spin" /> Analyzing…</> : <><Sparkles size={18} /> {submitError ? 'Try again' : 'Analyze meal'}</>}
          </button>
          <p className="text-[11px] text-muted-foreground text-center mt-2" data-testid="ai-estimate-disclosure">
            AI nutrition estimates may be inaccurate. Review ingredients, portions, and nutrition before logging.
          </p>
        </div>
        {/* Keyboard open only: room to scroll the description + Analyze above the keyboard. */}
        {kb > 0 && <div aria-hidden="true" style={{ height: kb }} data-testid="keyboard-spacer" />}
      </div>
    </div>
  );
}
